import type { ISlideshowConfigRequest, ISlideshowConfigResponse } from "@/types"
import { EventVault } from "./eventVault"

export enum SlideshowConfigVideoPlacement {
  Mixed = "Mixed",
  End = "AtEnd",
  Start = "AtStart",
}

function slideshowConfigVideoPlacementFromResponse(responseValue?: string) {
  switch (responseValue) {
    case "Mixed":
      return SlideshowConfigVideoPlacement.Mixed
    case "AtEnd":
      return SlideshowConfigVideoPlacement.End
    case "AtStart":
      return SlideshowConfigVideoPlacement.Start
    default:
      return SlideshowConfigVideoPlacement.Mixed
  }
}

export enum SlideshowConfigType {
  All = "All",
  Pics = "PhotosOnly",
  Vids = "VideosOnly",
}

function slideshowConfigTypeFromResponse(responseValue?: string) {
  switch (responseValue) {
    case "All":
      return SlideshowConfigType.All
    case "PhotosOnly":
      return SlideshowConfigType.Pics
    case "VideosOnly":
      return SlideshowConfigType.Vids
    default:
      return SlideshowConfigType.All
  }
}

export enum SlideshowConfigSortBy {
  DateTaken = "DateTaken",
  DateUploaded = "DateCreated",
  Name = "Name",
  Random = "Randomized",
  Popularity = "Reactions",
  AlbumOrder = "AlbumOrder",
}

function slideshowConfigSortByFromResponse(responseValue?: string) {
  switch (responseValue) {
    case "DateTaken":
      return SlideshowConfigSortBy.DateTaken
    case "DateCreated":
      return SlideshowConfigSortBy.DateUploaded
    case "Name":
      return SlideshowConfigSortBy.Name
    case "Randomized":
      return SlideshowConfigSortBy.Random
    case "Reactions":
      return SlideshowConfigSortBy.Popularity
    case "AlbumOrder":
      return SlideshowConfigSortBy.AlbumOrder
    default:
      return undefined
  }
}

export enum SlideshowConfigOrderBy {
  Asc = "Asc",
  Desc = "Desc",
}

function slideshowConfigOrderByFromResponse(responseValue?: string) {
  switch (responseValue) {
    case "Asc":
      return SlideshowConfigOrderBy.Asc
    case "Desc":
      return SlideshowConfigOrderBy.Desc
    default:
      return undefined
  }
}

export class SlideshowConfig {
  public id?: string
  public name?: string
  public isPublic?: boolean = false
  public interval?: number = 5000
  public maxSlides?: number = 100
  public live?: boolean = false
  public favourites?: boolean = false
  public enableDetails?: boolean = false
  public enableTitleSlide?: boolean = false
  public enableQrInTitleSlide?: boolean = false
  public titleSlideDurationMs?: number
  public backgroundColour?: string
  public videoPlacement?: SlideshowConfigVideoPlacement = SlideshowConfigVideoPlacement.Mixed
  public type?: SlideshowConfigType = SlideshowConfigType.All
  public orderBy?: SlideshowConfigOrderBy = SlideshowConfigOrderBy.Desc
  public sortBy?: SlideshowConfigSortBy = SlideshowConfigSortBy.DateTaken
  public mediaCount?: number
  public albums: number[] = []
  public tags: string[] = []
  public lastUpdated?: Date

  public eventVault?: EventVault

  constructor(config: Partial<SlideshowConfig>) {
    this.id = config.id
    this.name = config.name
    this.isPublic = config.isPublic ?? false
    this.interval = config.interval ?? 5000
    this.videoPlacement = config.videoPlacement
    this.maxSlides = config.maxSlides ?? 100
    this.live = config.live ?? false
    this.favourites = config.favourites ?? false
    this.enableDetails = config.enableDetails ?? false
    this.enableTitleSlide = config.enableTitleSlide ?? false
    this.enableQrInTitleSlide = config.enableQrInTitleSlide ?? false
    this.titleSlideDurationMs = config.titleSlideDurationMs
    this.backgroundColour = config.backgroundColour
    this.type = config.type
    this.orderBy = config.orderBy
    this.sortBy = config.sortBy
    this.mediaCount = config.mediaCount ?? 0
    this.albums = config.albums ?? []
    this.tags = config.tags ?? []
    this.lastUpdated = config.lastUpdated

    this.eventVault = config.eventVault
  }

  public static fromResponse(response?: ISlideshowConfigResponse) {
    const type = slideshowConfigTypeFromResponse(response?.settings?.type)
    const videoPlacement = slideshowConfigVideoPlacementFromResponse(response?.settings?.videoPlacement)
    const orderBy = slideshowConfigOrderByFromResponse(response?.settings?.orderBy)
    const sortBy = slideshowConfigSortByFromResponse(response?.settings?.sortBy)

    const eventVault = response?.eventVault ? EventVault.fromResponseV1(response.eventVault) : undefined

    return new SlideshowConfig({
      id: response?.sessionID,
      name: response?.name,
      lastUpdated: response?.lastUpdated ? new Date(response?.lastUpdated) : undefined,
      isPublic: response?.isPublic ?? false,
      interval: response?.settings?.interval,
      maxSlides: response?.settings?.maxSlides,
      enableDetails: response?.settings?.details ?? false,
      enableTitleSlide: response?.settings?.enableTitleSlide,
      enableQrInTitleSlide: response?.settings?.enableQrInTitleSlide,
      titleSlideDurationMs: response?.settings?.titleSlideDurationMs,
      favourites: response?.settings?.isPinned ?? false,
      live: response?.settings?.liveRefresh ?? false,
      backgroundColour: response?.settings?.backgroundColor ?? undefined,
      albums: response?.settings?.albums ?? [],
      tags: response?.settings?.tags ?? [],
      mediaCount: response?.mediaCount ?? 0,
      type,
      videoPlacement,
      orderBy,
      sortBy,
      eventVault,
    })
  }

  public toRequest(): ISlideshowConfigRequest {
    return {
      name: this.name,
      settings: {
        interval: this.interval,
        maxSlides: this.maxSlides,
        details: this.enableDetails,
        isPinned: this.favourites,
        liveRefresh: this.live,
        type: this.type,
        videoPlacement: this.videoPlacement,
        orderBy: this.orderBy,
        sortBy: this.sortBy,
        backgroundColor: this.backgroundColour,
        albums: this.albums,
        tags: this.tags,
        enableTitleSlide: this.enableTitleSlide,
        enableQrInTitleSlide: this.enableQrInTitleSlide,
        titleSlideDurationMs: this.titleSlideDurationMs,
      },
    }
  }
}
